import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  ChevronRight,
  Plus,
  Settings,
  ArrowUpDown,
  Filter,
  Calendar,
  CalendarDays,
  CalendarRange,
  Circle,
  CircleDot,
  CircleCheckBig,
  X,
} from "lucide-react";
import ListTaskRow from "@/components/tasks/lists/ListTaskRow/ListTaskRow";
import ListFilterPills from "@/components/tasks/options/ListFilterPills/ListFilterPills";
import ListTemplateRow from "@/components/tasks/lists/ListTemplateRow/ListTemplateRow";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import BulkTaskActionBar from "@/components/navigation/BulkTaskActionBar/BulkTaskActionBar";
import CreateTaskSheet from "@/components/tasks/sheets/CreateTaskSheet/CreateTaskSheet";
import PatternAddDialog, { type PatternAddDialogState } from "@/components/tasks/patterns/PatternAddDialog/PatternAddDialog";
import {
  createPatternAddSection,
  patternSectionToSelection,
  PatternTemplate,
} from "@/components/tasks/patterns/PatternAddSection/PatternAddSection";
import TaskSwipeActions from "@/components/tasks/lists/TaskSwipeActions/TaskSwipeActions";
import TaskStatusDivider, {
  shouldShowStatusDivider,
  statusCount,
  sortTasksByStatus,
} from "@/components/tasks/lists/TaskStatusDivider/TaskStatusDivider";
import TaskFilterDialog from "@/components/tasks/options/TaskFilterDialog/TaskFilterDialog";
import { Button } from "@/components/ui/button";
import { PageViewOptionsBar } from "@/components/navigation/PageViewOptionsBar/PageViewOptionsBar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DEFAULT_AREA_COLOR, getListColor } from "@/lib/listColors";
import { listSlug } from "@/lib/listSlug";
import { selectRange, toggleOne } from "@/lib/bulkSelection";
import { formatDateKey, nowTimeString, parseDateKey, todayKey } from "@/lib/time";
import { trpc } from "@/lib/trpc";
import {
  deriveTaskLifecycle,
  getTaskLifecycleLabel,
  type PersistedTaskStatus,
  type TaskLifecycle,
} from "@shared/taskLifecycle";

type AreaDateRange = "today" | "week" | "month" | "everything";
type AreaDoneAgeFilter = "never" | "1d" | "3d" | "1w" | "2w" | "1m";
type AreaItemFilter = "tasks" | "patterns";
type AreaDisplayParam = AreaItemFilter | null;

const STATUS_OPTIONS: Array<{ value: TaskLifecycle; label: string }> = [
  { value: "pending", label: getTaskLifecycleLabel("pending") },
  { value: "doing", label: getTaskLifecycleLabel("doing") },
  { value: "done", label: getTaskLifecycleLabel("done") },
  { value: "skipped", label: getTaskLifecycleLabel("skipped") },
];
const DEFAULT_STATUS_FILTER: TaskLifecycle[] = STATUS_OPTIONS.map(option => option.value);
const ITEM_OPTIONS: Array<{ value: AreaItemFilter; label: string }> = [
  { value: "tasks", label: "Tasks" },
  { value: "patterns", label: "Patterns" },
];
const DEFAULT_ITEM_FILTER: AreaItemFilter[] = ITEM_OPTIONS.map(option => option.value);
const AREA_STATUS_COLLAPSED_KEY = "lists-page-status-collapsed-v2";
const RANGE_OPTIONS: Array<{ value: AreaDateRange; label: string }> = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "everything", label: "Everything" },
];
const DONE_AGE_OPTIONS: Array<{ value: AreaDoneAgeFilter; label: string; days: number | null }> = [
  { value: "never", label: "Never", days: null },
  { value: "1d", label: "1 day", days: 1 },
  { value: "3d", label: "3 days", days: 3 },
  { value: "1w", label: "1 week", days: 7 },
  { value: "2w", label: "2 weeks", days: 14 },
  { value: "1m", label: "1 month", days: 30 },
];

function StatusIcon({ status, className = "h-4 w-4" }: { status: TaskLifecycle; className?: string }) {
  if (status === "doing") return <CircleDot className={className} aria-label={getTaskLifecycleLabel(status)} />;
  if (status === "done") return <CircleCheckBig className={className} aria-label="Done" />;
  if (status === "skipped") return <X className={className} aria-label="Skipped" />;
  return <Circle className={className} aria-label="Pending" />;
}

function RangeIcon({ range, className = "h-4 w-4" }: { range: AreaDateRange; className?: string }) {
  if (range === "week") return <CalendarRange className={className} aria-label="This week" />;
  if (range === "month") return <CalendarDays className={className} aria-label="This month" />;
  if (range === "everything") return null;
  return <Calendar className={className} aria-label="Today" />;
}

function toStatusFilter(value: unknown): TaskLifecycle[] {
  if (!Array.isArray(value)) return DEFAULT_STATUS_FILTER;
  const statuses = value.filter((item): item is TaskLifecycle =>
    item === "pending" || item === "doing" || item === "done" || item === "skipped",
  );
  return statuses.length > 0 ? statuses : DEFAULT_STATUS_FILTER;
}

function toItemFilter(value: unknown): AreaItemFilter[] {
  if (!Array.isArray(value)) return DEFAULT_ITEM_FILTER;
  const items = value.filter((item): item is AreaItemFilter =>
    item === "tasks" || item === "patterns",
  );
  return items.length > 0 ? items : DEFAULT_ITEM_FILTER;
}

function toAreaDateRange(value: unknown): AreaDateRange {
  return value === "today" || value === "week" || value === "month"
    ? value
    : "everything";
}

function toAreaDoneAgeFilter(value: unknown): AreaDoneAgeFilter {
  return value === "1d" || value === "3d" || value === "1w" || value === "2w" || value === "1m"
    ? value
    : "never";
}

function isDateKey(value: string | null): value is string {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function toDisplayParam(value: string | null): AreaDisplayParam {
  if (value === "tasks" || value === "task") return "tasks";
  if (value === "patterns" || value === "pattern" || value === "templates" || value === "template") {
    return "patterns";
  }
  return null;
}

function doneAgeCutoff(filter: AreaDoneAgeFilter, today: string) {
  const option = DONE_AGE_OPTIONS.find(item => item.value === filter);
  if (!option?.days) return null;
  const cutoff = parseDateKey(today);
  cutoff.setDate(cutoff.getDate() - option.days);
  cutoff.setHours(0, 0, 0, 0);
  return cutoff;
}

function doneTimestamp(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function shouldShowTaskByDoneAge(
  task: { status: PersistedTaskStatus; completedAt?: Date | string | null; startedAt?: Date | string | null },
  cutoff: Date | null,
) {
  if (!cutoff || deriveTaskLifecycle(task) !== "done") return true;
  const doneAt = doneTimestamp(task.completedAt) ?? doneTimestamp(task.startedAt);
  if (!doneAt) return true;
  return doneAt >= cutoff;
}

function areaRangeBounds(range: AreaDateRange, today: string) {
  const date = parseDateKey(today);
  if (range === "everything") return { startDate: null, endDate: null };
  if (range === "today") return { startDate: today, endDate: today };
  if (range === "week") {
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { startDate: formatDateKey(start), endDate: formatDateKey(end) };
  }
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { startDate: formatDateKey(start), endDate: formatDateKey(end) };
}

export default function Lists() {
  const [location, setLocation] = useLocation();
  const [selectedListSlug, setSelectedListSlug] = useState<string | null>(null);
  const today = todayKey();
  const urlFilters = useMemo(() => {
    const query = location.includes("?")
      ? location.slice(location.indexOf("?") + 1)
      : typeof window !== "undefined"
        ? window.location.search.replace(/^\?/, "")
        : "";
    const params = new URLSearchParams(query);
    const day = params.get("day");
    return {
      list: params.get("list"),
      day: isDateKey(day) ? day : null,
      display: toDisplayParam(params.get("display")),
    };
  }, [location]);
  useEffect(() => {
    setSelectedListSlug(urlFilters.list);
  }, [urlFilters.list]);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [createSheetListId, setCreateSheetListId] = useState<number | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
  const [patternSheet, setPatternSheet] = useState<PatternAddDialogState | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    if (typeof localStorage === "undefined") return new Set();
    const stored = localStorage.getItem(AREA_STATUS_COLLAPSED_KEY);
    if (!stored) return new Set();
    try {
      const values = JSON.parse(stored);
      return Array.isArray(values) ? new Set(values.filter(value => typeof value === "string")) : new Set();
    } catch {
      return new Set();
    }
  });
  const [collapsedGroupsHydrated, setCollapsedGroupsHydrated] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(AREA_STATUS_COLLAPSED_KEY) !== null;
  });
  const templatesQuery = trpc.templates.listAll.useQuery();
  const utils = trpc.useUtils();
  const statusFilterQuery = trpc.preferences.listStatusFilter.useQuery();
  const setStatusFilter = trpc.preferences.setListStatusFilter.useMutation();
  const dateRangeQuery = trpc.preferences.listDateRange.useQuery();
  const setDateRange = trpc.preferences.setListDateRange.useMutation();
  const doneAgeFilterQuery = trpc.preferences.listDoneAgeFilter.useQuery();
  const setDoneAgeFilter = trpc.preferences.setListDoneAgeFilter.useMutation();
  const itemFilterQuery = trpc.preferences.listItemFilter.useQuery();
  const setItemFilter = trpc.preferences.setListItemFilter.useMutation();
  const selectedRange = toAreaDateRange(dateRangeQuery.data?.range);
  const selectedDoneAgeFilter = toAreaDoneAgeFilter(doneAgeFilterQuery.data?.filter);
  const selectedItems = urlFilters.display
    ? [urlFilters.display]
    : toItemFilter(itemFilterQuery.data?.items);
  const selectedItemSet = new Set(selectedItems);
  const showTasks = selectedItemSet.has("tasks");
  const showPatterns = selectedItemSet.has("patterns");
  const hiddenItems = ITEM_OPTIONS
    .map(option => option.value)
    .filter(item => !selectedItemSet.has(item));
  const doneCutoff = useMemo(
    () => doneAgeCutoff(selectedDoneAgeFilter, today),
    [selectedDoneAgeFilter, today],
  );
  const rangeBounds = useMemo(
    () => urlFilters.day
      ? { startDate: urlFilters.day, endDate: urlFilters.day }
      : areaRangeBounds(selectedRange, today),
    [selectedRange, today, urlFilters.day],
  );
  const overview = trpc.lists.overview.useQuery({
    today,
    startDate: rangeBounds.startDate,
    endDate: rangeBounds.endDate,
  });
  const lists = overview.data?.lists ?? [];
  useEffect(() => {
    if (collapsedGroupsHydrated || lists.length === 0) return;
    setCollapsedGroups(new Set(lists.flatMap(list => [`${list.id}:done`, `${list.id}:skipped`])));
    setCollapsedGroupsHydrated(true);
  }, [lists, collapsedGroupsHydrated]);

  useEffect(() => {
    if (typeof localStorage === "undefined" || !collapsedGroupsHydrated) return;
    localStorage.setItem(AREA_STATUS_COLLAPSED_KEY, JSON.stringify(Array.from(collapsedGroups)));
  }, [collapsedGroups, collapsedGroupsHydrated]);
  const visibleLists = useMemo(
    () =>
      selectedListSlug
        ? lists.filter(list =>
            selectedListSlug === "inbox"
              ? list.kind === "inbox"
              : listSlug(list.name) === selectedListSlug,
          )
        : lists,
    [lists, selectedListSlug],
  );
  const selectedListIds = useMemo(
    () =>
      selectedListSlug
        ? lists
            .filter(list =>
              selectedListSlug === "inbox"
                ? list.kind === "inbox"
                : listSlug(list.name) === selectedListSlug,
            )
            .map(list => list.id)
        : [],
    [lists, selectedListSlug],
  );
  const tasks = useMemo(
    () =>
      urlFilters.day
        ? (overview.data?.tasks ?? []).filter(task => task.date === urlFilters.day)
        : (overview.data?.tasks ?? []),
    [overview.data?.tasks, urlFilters.day],
  );
  const templates = templatesQuery.data ?? [];
  const selectedStatuses = toStatusFilter(statusFilterQuery.data?.statuses);
  const selectedStatusSet = new Set(selectedStatuses);
  const hiddenStatuses = STATUS_OPTIONS
    .map(option => option.value)
    .filter(status => !selectedStatusSet.has(status));
  const visibleAreaTaskIds = useMemo(
    () =>
      visibleLists.flatMap(list => {
        const visibleAreaTasks = sortTasksByStatus(
          tasks
            .filter(task =>
              task.listId === list.id ||
              (list.kind === "inbox" && task.listId === null),
            )
            .filter(task => selectedStatusSet.has(deriveTaskLifecycle(task)))
            .filter(task => shouldShowTaskByDoneAge(task, doneCutoff)),
        );
        return visibleAreaTasks
          .filter(task => !collapsedGroups.has(`${list.id}:${deriveTaskLifecycle(task)}`))
          .map(task => task.id);
      }),
    [collapsedGroups, doneCutoff, selectedStatuses, tasks, visibleLists],
  );
  const markDone = trpc.tasks.markDone.useMutation({
    onSuccess: () => {
      utils.lists.overview.invalidate();
      utils.tasks.listByList.invalidate();
      utils.day.monthSummary.invalidate();
    },
  });
  const toggleSkipped = trpc.tasks.toggleSkipped.useMutation({
    onSuccess: () => {
      utils.lists.overview.invalidate();
      utils.tasks.listByList.invalidate();
      utils.day.monthSummary.invalidate();
    },
  });
  const activeDate = urlFilters.day ?? today;
  const addToToday = trpc.tasks.update.useMutation({
    onSuccess: (_data, variables) => {
      const targetDate = variables.date ?? activeDate;
      utils.lists.overview.invalidate();
      utils.tasks.listByList.invalidate();
      utils.day.list.invalidate({ date: targetDate });
      utils.day.monthSummary.invalidate();
    },
  });
  const duplicateTask = trpc.tasks.duplicate.useMutation({
    onSuccess: (_data, vars) => {
      utils.lists.overview.invalidate();
      utils.tasks.listByList.invalidate();
      if (vars.date) utils.day.list.invalidate({ date: vars.date });
      utils.day.monthSummary.invalidate();
    },
  });
  const addPatternToToday = trpc.day.addListToDay.useMutation({
    onSuccess: (_data, variables) => {
      utils.lists.overview.invalidate();
      utils.tasks.listByList.invalidate();
      utils.day.list.invalidate({ date: variables.date });
      utils.day.monthSummary.invalidate();
    },
  });
  function chooseSelected(id: number, visibleIds: number[], shiftKey = false) {
    setSelected(prev =>
      shiftKey && lastSelectedId !== null
        ? selectRange(prev, visibleIds, lastSelectedId, id)
        : toggleOne(prev, id),
    );
    setLastSelectedId(id);
  }

  function startSelectMode(id: number) {
    setSelectMode(true);
    setSelected(prev => new Set(prev).add(id));
    setLastSelectedId(id);
  }

  function addPattern(listId: number, section: PatternAddDialogState["section"]) {
    return addPatternToToday.mutateAsync({
      date: activeDate,
      listId,
      fallbackStartTime: nowTimeString(),
      selections: [patternSectionToSelection({ ...section, selected: true })],
    }).then(result => {
      setPatternSheet(null);
      return result;
    });
  }

  function openPattern(list: {
    id: number;
    name: string;
    color: string | null;
    bgColor?: string | null;
  }, pattern: PatternTemplate) {
    setPatternSheet({
      listId: list.id,
      listName: list.name,
      listColor: list.color,
      listBgColor: list.bgColor ?? null,
      section: createPatternAddSection(pattern, nowTimeString(), {
        selected: true,
        expanded: true,
      }),
    });
  }

  function toggleGroup(key: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleStatusFilter(status: TaskLifecycle) {
    const next = selectedStatusSet.has(status)
      ? selectedStatuses.filter(item => item !== status)
      : [...selectedStatuses, status];
    utils.preferences.listStatusFilter.setData(undefined, { statuses: next });
    setStatusFilter.mutate(
      { statuses: next },
      {
        onError: () => {
          utils.preferences.listStatusFilter.invalidate();
        },
      },
    );
  }

  function chooseDateRange(range: AreaDateRange) {
    utils.preferences.listDateRange.setData(undefined, { range });
    setDateRange.mutate(
      { range },
      {
        onError: () => {
          utils.preferences.listDateRange.invalidate();
        },
      },
    );
  }

  function chooseDoneAgeFilter(filter: AreaDoneAgeFilter) {
    utils.preferences.listDoneAgeFilter.setData(undefined, { filter });
    setDoneAgeFilter.mutate(
      { filter },
      {
        onError: () => {
          utils.preferences.listDoneAgeFilter.invalidate();
        },
      },
    );
  }

  function toggleListFilter(listId: number) {
    const list = lists.find(item => item.id === listId);
    if (!list) return;
    const nextParams = new URLSearchParams();
    if (urlFilters.day) nextParams.set("day", urlFilters.day);
    if (urlFilters.display) nextParams.set("display", urlFilters.display);
    const nextArea = list.kind === "inbox" ? "inbox" : listSlug(list.name);
    const nextSelectedArea = selectedListSlug === nextArea ? null : nextArea;
    setSelectedListSlug(nextSelectedArea);
    if (nextSelectedArea) nextParams.set("list", nextSelectedArea);
    const query = nextParams.toString();
    setLocation(query ? `/lists?${query}` : "/lists", { replace: true });
  }

  function toggleItemFilter(item: AreaItemFilter) {
    const next = selectedItemSet.has(item)
      ? selectedItems.filter(value => value !== item)
      : [...selectedItems, item];
    if (next.length === 0) return;
    utils.preferences.listItemFilter.setData(undefined, { items: next });
    setItemFilter.mutate(
      { items: next },
      {
        onError: () => {
          utils.preferences.listItemFilter.invalidate();
        },
      },
    );
  }

  return (
    <AppShell title="Lists">
      <div className="container max-w-3xl py-6 space-y-6">
        <header className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
              Lists
            </h1>
            {(urlFilters.list || urlFilters.day || urlFilters.display) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {[
                  urlFilters.display === "tasks" ? "Tasks" : null,
                  urlFilters.display === "patterns" ? "Patterns" : null,
                  urlFilters.list ? `List: ${urlFilters.list}` : null,
                  urlFilters.day ? `Day: ${urlFilters.day}` : null,
                ].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 rounded-full"
            onClick={() => setLocation("/settings/lists")}
          >
            <Settings className="h-4 w-4" />
            Edit
          </Button>
        </header>

        <ListFilterPills
          lists={lists}
          loading={overview.isLoading}
          selectedListIds={selectedListIds}
          onSelect={toggleListFilter}
          purpose="lists"
        />

        <PageViewOptionsBar
          leftSlot={
            <button
              onClick={() => setFilterOpen(true)}
              className={`inline-flex items-center gap-1.5 rounded-full p-2 transition-colors ${
                hiddenStatuses.length === 0 &&
                hiddenItems.length === 0 &&
                selectedRange === "everything" &&
                selectedDoneAgeFilter === "never" &&
                !urlFilters.list &&
                !urlFilters.day &&
                !urlFilters.display
                  ? "text-muted-foreground hover:text-foreground"
                  : "text-foreground bg-accent"
              }`}
              title="Filter"
            >
              <Filter className="h-4 w-4" />
              {(selectedRange !== "everything" || hiddenStatuses.length > 0 || urlFilters.day || urlFilters.display) && (
                <span className="mx-0.5 h-3.5 w-px bg-border" aria-hidden />
              )}
              {urlFilters.day ? (
                <Calendar className="h-3 w-3" aria-label="Selected day" />
              ) : selectedRange !== "everything" && (
                <RangeIcon range={selectedRange} className="h-3 w-3" />
              )}
              {urlFilters.display === "tasks" && (
                <span className="text-[10px] font-semibold uppercase leading-none">T</span>
              )}
              {urlFilters.display === "patterns" && (
                <span className="text-[10px] font-semibold uppercase leading-none">P</span>
              )}
              {hiddenStatuses.map(status => (
                <StatusIcon key={status} status={status} className="h-3 w-3 opacity-70" />
              ))}
            </button>
          }
          rightSlot={
            <button
              onClick={() => setSortOpen(true)}
              className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors"
              title="Sort"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
          }
        />

        <TaskFilterDialog
          open={filterOpen}
          onOpenChange={setFilterOpen}
          itemFilter={{
            title: "Show",
            options: ITEM_OPTIONS,
            selected: selectedItemSet,
            onToggle: toggleItemFilter,
          }}
          rangeFilter={{
            title: "Range",
            options: RANGE_OPTIONS.map(option => ({
              ...option,
              icon: <RangeIcon range={option.value} className="h-4 w-4" />,
            })),
            selected: selectedRange,
            onChoose: chooseDateRange,
          }}
          statusFilter={{
            title: "Status",
            options: STATUS_OPTIONS.map(option => ({
              ...option,
              icon: <StatusIcon status={option.value} className="h-4 w-4" />,
            })),
            selected: selectedStatusSet,
            onToggle: toggleStatusFilter,
          }}
          doneAgeFilter={{
            title: "Hide done older than",
            options: DONE_AGE_OPTIONS,
            selected: selectedDoneAgeFilter,
            onChoose: chooseDoneAgeFilter,
          }}
        />

        <Dialog open={sortOpen} onOpenChange={setSortOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Sort</DialogTitle>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        <PatternAddDialog
          value={patternSheet}
          pending={addPatternToToday.isPending}
          onChange={setPatternSheet}
          onClose={() => setPatternSheet(null)}
          onAdd={sheet => addPattern(sheet.listId, sheet.section)}
        />

        {overview.isLoading || templatesQuery.isLoading ? (
          <div className="space-y-4">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-28 rounded-xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : visibleLists.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center">
            <div className="font-display text-xl">
              {lists.length === 0 ? "No lists yet" : "No lists match"}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {lists.length === 0
                ? "Add a list in settings to start grouping tasks."
                : "Adjust the filters to see more lists."}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {visibleLists.map((list, index) => {
              const colors = getListColor(list.color ?? DEFAULT_AREA_COLOR);
              const previous = visibleLists[index - 1];
              const categoryLabel = list.kind === "inbox"
                ? "Inbox"
                : list.categoryName ?? "Uncategorized";
              const previousCategoryLabel = previous?.kind === "inbox"
                ? "Inbox"
                : previous?.categoryName ?? "Uncategorized";
              const showCategoryHeader = index === 0 || categoryLabel !== previousCategoryLabel;
              const allAreaTasks = tasks.filter(t =>
                  t.listId === list.id || (list.kind === "inbox" && t.listId === null),
                );
              const areaTasks = sortTasksByStatus(
                allAreaTasks
                  .filter(t => selectedStatusSet.has(deriveTaskLifecycle(t)))
                  .filter(t => shouldShowTaskByDoneAge(t, doneCutoff)),
              );
              const areaTemplates = templates.filter(t => t.listId === list.id);
              const categorySegment = list.categoryName ? `${listSlug(list.categoryName)}/` : "";
              const href = list.kind === "inbox"
                ? "/lists/inbox"
                : `/lists/${categorySegment}${listSlug(list.name)}`;
              return (
                <section key={list.id} className="space-y-2">
                  {showCategoryHeader && (
                    <div className="px-2 pt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      {categoryLabel}
                    </div>
                  )}
                  <div className="flex items-center gap-1 rounded-xl hover:bg-accent/40 transition-colors">
                    <button
                      type="button"
                      onClick={() => setLocation(href)}
                      className="min-w-0 flex-1 flex items-center justify-between gap-3 rounded-xl px-2 py-2 text-left"
                    >
                      <span className="min-w-0 flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ background: colors.base }}
                          aria-hidden
                        />
                        <span className="font-display text-xl truncate">{list.name}</span>
                        {showTasks && (
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {areaTasks.length} tasks
                          </span>
                        )}
                        {showPatterns && (
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {areaTemplates.length} patterns
                          </span>
                        )}
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    {!showTasks ? null : areaTasks.length === 0 ? (
                      <div className="rounded-xl border border-border/60 bg-card px-3 py-3 text-sm text-muted-foreground">
                        {allAreaTasks.length === 0 ? "Nothing upcoming." : "No tasks match this filter."}
                      </div>
                    ) : (
                      <ul className="task-list divide-y divide-border/50 border border-border/60 rounded-xl overflow-hidden bg-card">
                        {areaTasks.map((t, index) => {
                          const lifecycle = deriveTaskLifecycle(t);
                          return <li key={t.id} className="task">
                            {(() => {
                              const groupKey = `${list.id}:${lifecycle}`;
                              const collapsed = collapsedGroups.has(groupKey);
                              return (
                                <>
                                  {shouldShowStatusDivider(areaTasks, index) && (
                                    <TaskStatusDivider
                                      status={lifecycle}
                                      count={statusCount(areaTasks, lifecycle)}
                                      collapsed={collapsed}
                                      onToggle={() => toggleGroup(groupKey)}
                                    />
                                  )}
                                  {!collapsed && (
                                    <TaskSwipeActions
                                      task={t}
                                      disabled={selectMode}
                                      onSwipeRight={() =>
                                        markDone.mutate({
                                          id: t.id,
                                          clientTimestamp: new Date().toISOString(),
                                        })
                                      }
                                      onSwipeLeft={() =>
                                        toggleSkipped.mutate({
                                          id: t.id,
                                          clientTimestamp: new Date().toISOString(),
                                        })
                                      }
                                      onLongPress={() => startSelectMode(t.id)}
                                    >
                                      <ListTaskRow
                                        task={{
                                          ...t,
                                          listColor: t.listColor ?? list.color,
                                          listBgColor: t.listBgColor ?? list.bgColor ?? null,
                                        }}
                                        variant="detailed"
                                        selected={selected.has(t.id)}
                                        selectMode={selectMode}
                                        onOpen={event => {
                                          if (selectMode) chooseSelected(t.id, visibleAreaTaskIds, event.shiftKey);
                                          else setLocation(`/task/${t.id}`);
                                        }}
                                        onAddToToday={() =>
                                          addToToday.mutate({
                                            id: t.id,
                                            date: activeDate,
                                            startTime: null,
                                            clientTimestamp: new Date().toISOString(),
                                          })
                                        }
                                        onDuplicate={() =>
                                          duplicateTask.mutate({
                                            id: t.id,
                                            date: t.date ?? null,
                                          })
                                        }
                                      />
                                    </TaskSwipeActions>
                                  )}
                                </>
                              );
                            })()}
                          </li>;
                        })}
                      </ul>
                    )}
                    {showTasks && (
                      <div className="flex px-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCreateSheetListId(list.id);
                            setCreateSheetOpen(true);
                          }}
                          className="inline-flex min-h-9 w-full items-center justify-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-opacity hover:opacity-80"
                          style={{
                            color: colors.ink,
                            backgroundColor: colors.soft,
                            borderColor: colors.base,
                          }}
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                          Add task
                        </button>
                      </div>
                    )}

                    {showPatterns && areaTemplates.length > 0 && (
                      <div className="divide-y divide-border/50 border border-border/60 rounded-xl overflow-hidden bg-card">
                        <div className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground bg-muted/30">
                          Patterns
                        </div>
                        {areaTemplates.map(t => (
                          <ListTemplateRow
                            key={t.id}
                            template={t}
                            variant="simple"
                            listColor={list.color}
                            listBgColor={list.bgColor ?? null}
                            onOpen={() => openPattern(list, t)}
                            onAddToToday={() =>
                              addPattern(
                                list.id,
                                createPatternAddSection(t, nowTimeString(), {
                                  selected: true,
                                  expanded: false,
                                }),
                              )
                            }
                          />
                        ))}
                      </div>
                    )}
                    {showPatterns && areaTemplates.length === 0 && !showTasks && (
                      <div className="rounded-xl border border-border/60 bg-card px-3 py-3 text-sm text-muted-foreground">
                        No patterns yet.
                      </div>
                    )}
                    {showPatterns && (
                      <div className="flex px-2">
                        <button
                          type="button"
                          onClick={() => setLocation(`/settings/lists/${list.id}?new=pattern`)}
                          className="inline-flex min-h-9 w-full items-center justify-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-opacity hover:opacity-80"
                          style={{
                            color: colors.ink,
                            backgroundColor: colors.soft,
                            borderColor: colors.base,
                          }}
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                          Add pattern
                        </button>
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
        {!selectMode && (
          <Button
            onClick={() => {
              setCreateSheetListId(null);
              setCreateSheetOpen(true);
            }}
            size="lg"
            className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full shadow-lg gap-0 p-0"
            aria-label="Add task"
          >
            <Plus className="h-5 w-5" />
          </Button>
        )}
        <CreateTaskSheet
          open={createSheetOpen}
          onOpenChange={open => {
            setCreateSheetOpen(open);
            if (!open) setCreateSheetListId(null);
          }}
          listId={createSheetListId}
        />
        {selectMode && (
          <BulkTaskActionBar
            selectedIds={Array.from(selected)}
            onComplete={() => {
              setSelectMode(false);
              setSelected(new Set());
              setLastSelectedId(null);
            }}
          />
        )}
      </div>
    </AppShell>
  );
}
